const LEGEND_POSITIONS = {
  top: { top: 4, left: 'center' },
  bottom: { bottom: 2, left: 'center' },
  left: { left: 4, top: 'middle', orient: 'vertical' },
  right: { right: 4, top: 'middle', orient: 'vertical' },
};

export function getLegendConfig(chart, options = {}) {
  const pos = LEGEND_POSITIONS[chart.legendPosition] || LEGEND_POSITIONS.bottom;
  const compact = options.compact || chart.compactLayout;
  return {
    show: true,
    type: 'scroll',
    ...pos,
    ...(compact
      ? {
          itemWidth: 12,
          itemHeight: 10,
          itemGap: 3,
          padding: [2, 4],
          textStyle: { fontSize: 10 },
          pageIconSize: 8,
          pageTextStyle: { fontSize: 9 },
        }
      : {}),
  };
}

const STACKED_TYPES = new Set(['stackedColumn', 'stackedBar']);

export function chartUsesFullHeight(chart) {
  if (!chart) return false;
  return !!(chart.chartFullHeight ?? chart.chart_full_height);
}

export function chartStackTo100Percent(chart) {
  if (!chart) return false;
  return !!(chart.stackTo100Percent ?? chart.stack_to_100_percent);
}

export function chartSupportsStackTo100(chart) {
  if (!chart) return false;
  const ct = resolveChartType(chart);
  return STACKED_CHART_TYPES.has(ct) || chart.matrixChart === true;
}

function parseStackNumeric(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Normalize each category's stack segments to sum to 100% (excludes Total series). */
export function applyStackTo100Percent(extracted) {
  if (!extracted?.series?.length) return extracted;
  const labelCount = (extracted.labels || []).length;
  const mainSeries = extracted.series.filter((s) => !s.isCategoryTotal);
  if (!mainSeries.length) return extracted;

  const updated = mainSeries.map((s) => ({
    ...s,
    data: (s.data || []).map((v) => parseStackNumeric(v)),
  }));

  for (let i = 0; i < labelCount; i++) {
    let sum = 0;
    updated.forEach((s) => {
      sum += parseStackNumeric(s.data[i]);
    });
    if (sum > 0) {
      updated.forEach((s) => {
        s.data[i] = (parseStackNumeric(s.data[i]) / sum) * 100;
      });
    } else {
      updated.forEach((s) => {
        s.data[i] = 0;
      });
    }
  }

  return { ...extracted, series: updated };
}

function resolveSeriesTypeForField(chart, seriesMeta) {
  return (
    chart.seriesTypes?.[seriesMeta.fieldName] ||
    seriesMeta.seriesType ||
    'bar'
  );
}

export function isPercentSeries(seriesMeta) {
  const dt = String(seriesMeta?.dataType || '').toLowerCase();
  if (dt === 'percent' || dt === 'percentage') return true;
  const label = `${seriesMeta?.fieldName || ''} ${seriesMeta?.name || ''}`.toLowerCase();
  return /(?:%|percent|percentage|rate|ratio|win\s*rate)/.test(label);
}

/** MoM / derived growth — not bounded to 0–100% like share or win-rate metrics. */
export function isGrowthPercentSeries(seriesMeta) {
  if (!seriesMeta) return false;
  if (seriesMeta.isGrowthPercent) return true;
  const derived = String(seriesMeta.derivedType || '').toLowerCase();
  if (derived === 'period_over_period') return true;
  const label = `${seriesMeta?.fieldName || ''} ${seriesMeta?.name || ''}`.toLowerCase();
  return /growth|period.over.period|mom|yoy/.test(label);
}

/** Share-of-total / win-rate style metrics that live on a 0–100% scale. */
export function isBoundedPercentSeries(seriesMeta) {
  return isPercentSeries(seriesMeta) && !isGrowthPercentSeries(seriesMeta);
}

/** Symmetric cap so extreme spikes (e.g. 1400% MoM) do not flatten the line. */
export const GROWTH_PERCENT_CHART_DISPLAY_CAP = 100;

export function prepareGrowthPercentChartData(values, cap = GROWTH_PERCENT_CHART_DISPLAY_CAP) {
  return (values || []).map((v) => {
    if (v == null || !Number.isFinite(v)) {
      return { value: null, actualValue: null, wasCapped: false };
    }
    const capped = Math.max(-cap, Math.min(cap, v));
    return { value: capped, actualValue: v, wasCapped: capped !== v };
  });
}

export function computeGrowthPercentAxisRange(
  preparedPoints,
  cap = GROWTH_PERCENT_CHART_DISPLAY_CAP
) {
  const values = (preparedPoints || [])
    .map((p) => p?.value)
    .filter((v) => Number.isFinite(v));
  if (!values.length) {
    return { min: -cap, max: cap };
  }
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min > 0) min = 0;
  if (max < 0) max = 0;
  const span = max - min || 1;
  const pad = Math.max(span * 0.12, 8);
  return {
    min: Math.floor(min - pad),
    max: Math.ceil(max + pad),
  };
}

function tooltipValueFromParam(param) {
  if (param?.data != null && typeof param.data === 'object') {
    if (param.data.actualValue != null && Number.isFinite(param.data.actualValue)) {
      return param.data.actualValue;
    }
    if (param.data.value != null) return param.data.value;
  }
  const v = param?.value;
  if (typeof v === 'object' && v != null) {
    if (v.actualValue != null) return v.actualValue;
    return v.value;
  }
  return v;
}

function formatMixedTooltipValue(value, seriesMeta, currencyFormatters) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  if (isPercentSeries(seriesMeta)) {
    return `${n.toFixed(1).replace(/\.0$/, '')}%`;
  }
  if (currencyFormatters?.formatChartValue) {
    return currencyFormatters.formatChartValue(n);
  }
  return n.toLocaleString();
}

/**
 * Render tooltips outside widget/chart overflow containers (dashboard, home, etc.).
 */
export function getTooltipOverflowConfig() {
  return {
    confine: false,
    appendTo: () =>
      typeof document !== 'undefined' ? document.body : undefined,
    extraCssText: 'z-index: 10050; pointer-events: none;',
  };
}

export function getTooltipConfig(chart, currencyFormatters = null, extracted = null) {
  const config = {
    show: chart.showTooltips !== false,
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    ...getTooltipOverflowConfig(),
  };

  const ct = resolveChartType(chart);

  if (ct === 'mixed' && extracted?.series?.length) {
    config.axisPointer = { type: 'cross' };
    config.formatter = (params) => {
      if (!Array.isArray(params) || params.length === 0) return '';
      const axisLabel = params[0].axisValueLabel ?? params[0].name ?? '';
      let lines = `<div style="font-weight:600;margin-bottom:6px">${axisLabel}</div>`;
      params.forEach((p) => {
        const meta = extracted.series.find((s) => s.name === p.seriesName);
        const raw = tooltipValueFromParam(p);
        lines += `${p.marker} ${p.seriesName}: <strong>${formatMixedTooltipValue(raw, meta, currencyFormatters)}</strong><br/>`;
      });
      return lines;
    };
  }

  const isStacked = STACKED_TYPES.has(ct) || chart.matrixChart;

  if (isStacked) {
    const percentMode = chartStackTo100Percent(chart);
    const formatVal = (value) => {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (!Number.isFinite(n)) return String(value ?? '');
      if (percentMode) {
        return `${n.toFixed(1).replace(/\.0$/, '')}%`;
      }
      if (currencyFormatters?.formatChartValue) {
        return currencyFormatters.formatChartValue(value);
      }
      return n.toLocaleString();
    };

    config.formatter = (params) => {
      if (!Array.isArray(params) || params.length === 0) return '';
      const axisLabel = params[0].axisValueLabel ?? params[0].name ?? '';
      const breakdown = params.filter((p) => p.seriesName !== 'Total');
      const totalParam = params.find((p) => p.seriesName === 'Total');

      let sum = 0;
      let lines = `<div style="font-weight:600;margin-bottom:6px">${axisLabel}</div>`;

      breakdown.forEach((p) => {
        const v = typeof p.value === 'number' ? p.value : parseFloat(p.value);
        if (Number.isFinite(v)) sum += v;
        lines += `${p.marker} ${p.seriesName}: <strong>${formatVal(v)}</strong><br/>`;
      });

      const totalValue = totalParam
        ? typeof totalParam.value === 'number'
          ? totalParam.value
          : parseFloat(totalParam.value)
        : sum;

      if (Number.isFinite(totalValue)) {
        lines += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(0,0,0,0.12)">`;
        lines += `Total: <strong>${formatVal(totalValue)}</strong></div>`;
      }

      return lines;
    };
  } else if (currencyFormatters?.formatChartValue) {
    config.valueFormatter = (value) => currencyFormatters.formatChartValue(value);
  }

  return config;
}

export function getLabelConfig(chart) {
  return {
    show: chart.showLabels === true,
    color: chart.labelColor || '#000000',
    fontSize: 11,
  };
}

const CHART_TYPE_ALIASES = {
  horizontalbar: 'horizontalBar',
  stackedcolumn: 'stackedColumn',
  stackedbar: 'stackedBar',
};

const STACKED_CHART_TYPES = new Set(['stackedColumn', 'stackedBar']);

/** Canonical chart type key (camelCase) for registry lookups. */
export function normalizeChartType(chartType) {
  const raw = String(chartType || 'bar').trim();
  const lower = raw.toLowerCase();
  return CHART_TYPE_ALIASES[lower] || raw;
}

/** Resolve chart type from chart object (camelCase or snake_case). */
export function resolveChartType(chart) {
  if (!chart) return 'bar';
  return normalizeChartType(chart.chartType || chart.chart_type);
}

/** Match report builder: echarts by default, chartjs for scatter. */
export function resolveChartEngine(chart) {
  const explicit = chart?.chartEngine || chart?.chart_engine;
  if (explicit) return explicit;
  return resolveChartType(chart) === 'scatter' ? 'chartjs' : 'echarts';
}

function truncateCategoryLabel(text, maxLen = 20) {
  const s = String(text ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

/**
 * Layout for category axes (column X or horizontal-bar Y) — avoids overlapping owner names.
 */
export function getCategoryAxisLayout(labels = []) {
  const labelCount = Array.isArray(labels) ? labels.length : 0;
  const strings = (labels || []).map((l) => String(l ?? ''));
  const maxLabelLen = strings.reduce((max, s) => Math.max(max, s.length), 0);

  const needsRotate = labelCount > 4 || maxLabelLen > 11;
  const rotate = needsRotate ? (labelCount > 7 ? 45 : 35) : 0;
  const maxChars =
    labelCount > 12 ? 10 : labelCount > 8 ? 14 : labelCount > 5 ? 16 : 22;
  const fontSize = labelCount > 10 ? 10 : 11;

  return {
    rotate,
    maxChars,
    fontSize,
    needsRotate,
    labelCount,
    maxLabelLen,
  };
}

function buildCategoryAxisLabelConfig(labels = [], { horizontal = false } = {}) {
  const layout = getCategoryAxisLayout(labels);
  if (horizontal) {
    return {
      interval: 0,
      margin: 10,
      hideOverlap: true,
      fontSize: layout.fontSize,
      overflow: 'truncate',
      width: Math.min(140, 60 + layout.maxLabelLen * 4),
      formatter: (value) => truncateCategoryLabel(value, Math.max(layout.maxChars, 18)),
    };
  }
  return {
    rotate: layout.rotate,
    interval: 0,
    margin: layout.rotate ? 12 : 8,
    hideOverlap: true,
    fontSize: layout.fontSize,
    overflow: 'truncate',
    width: layout.rotate ? 72 : layout.labelCount > 6 ? 88 : 120,
    formatter: (value) => truncateCategoryLabel(value, layout.maxChars),
  };
}

/** Grid padding — extra bottom/left when category labels rotate or are long. */
export function getCartesianGrid(chart, extracted, { horizontalCategories = false, compact = false } = {}) {
  const legendPos = chart.legendPosition || 'bottom';
  const legendBottom = legendPos === 'bottom';
  const labels = extracted?.labels || [];
  const layout = getCategoryAxisLayout(labels);
  const isCompact = compact || chart.compactLayout;

  let bottom = legendBottom ? (isCompact ? '20%' : '14%') : isCompact ? '14%' : '10%';
  let left = '2%';

  if (!horizontalCategories && layout.needsRotate) {
    bottom = legendBottom
      ? layout.labelCount > 8
        ? '26%'
        : '22%'
      : layout.labelCount > 8
        ? '20%'
        : '16%';
  } else if (!horizontalCategories) {
    bottom = legendBottom ? '18%' : '12%';
  }

  if (horizontalCategories) {
    const leftPct = Math.min(28, 10 + Math.min(layout.maxLabelLen, 24) * 0.45);
    left = `${leftPct}%`;
    if (extracted?.yAxisLabel) {
      bottom = legendBottom ? '20%' : '16%';
    }
  } else if (extracted?.yAxisLabel) {
    left = '14%';
  }

  const top = legendPos === 'top' ? (isCompact ? '22%' : '16%') : isCompact ? '10%' : '12%';

  return {
    left,
    right: extracted?.stackByLabel ? '12%' : '3%',
    top,
    bottom,
    containLabel: true,
  };
}

/** Shared X/Y axes for vertical bar/column/line charts. */
export function buildVerticalCartesianAxes(
  extracted,
  chart,
  options = {},
  currencyFormatters = null
) {
  const labels = extracted.labels || [];
  const categoryLayout = getCategoryAxisLayout(labels);
  const legendBottom = !chart.legendPosition || chart.legendPosition === 'bottom';
  const xNameGap = legendBottom
    ? categoryLayout.needsRotate
      ? 58
      : 38
    : categoryLayout.needsRotate
      ? 48
      : 28;
  const percentAxis = chartStackTo100Percent(chart);
  const valueAxisLabel = percentAxis
    ? {
      formatter: (v) => {
        const n = typeof v === 'number' ? v : parseFloat(v);
        return Number.isFinite(n) ? `${n}%` : '';
      },
      margin: 8,
      hideOverlap: true,
    }
    : currencyFormatters?.formatChartAxisValue
      ? { formatter: (v) => currencyFormatters.formatChartAxisValue(v) }
      : { margin: 8, hideOverlap: true };

  return {
    xAxis: {
      type: 'category',
      data: labels,
      name: extracted.xAxisLabel || '',
      nameLocation: 'middle',
      nameGap: xNameGap,
      axisLabel: buildCategoryAxisLabelConfig(labels),
      axisTick: {
        alignWithLabel: true,
        interval: 0,
      },
      ...(options.boundaryGap !== undefined ? { boundaryGap: options.boundaryGap } : {}),
    },
    yAxis: {
      type: 'value',
      name: extracted.yAxisLabel || '',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 80,
      max: percentAxis ? 100 : undefined,
      nameTextStyle: {
        fontWeight: 'normal',
        fontSize: 12,
      },
      axisLabel: valueAxisLabel,
    },
  };
}

export function buildCartesianSeries(
  extracted,
  chart,
  { horizontal = false, defaultType = 'bar', currencyFormatters = null } = {}
) {
  const ct = resolveChartType(chart);
  const isArea = ct === 'area';
  const isMixed = ct === 'mixed';
  const isStackedType = STACKED_CHART_TYPES.has(ct);

  return extracted.series.map((s) => {
    let type = defaultType;
    if (isMixed) {
      type =
        resolveSeriesTypeForField(chart, s) === 'line' || s.seriesType === 'line'
          ? 'line'
          : 'bar';
    } else if (ct === 'line' || ct === 'area') {
      type = 'line';
    } else if (ct === 'bar' || ct === 'horizontalBar' || isStackedType) {
      type = 'bar';
    }

    const isCategoryTotal = Boolean(s.isCategoryTotal);
    const shouldStack =
      (isStackedType || chart.matrixChart) && type === 'bar';
    const stackId = shouldStack
      ? isCategoryTotal
        ? 'categoryTotal'
        : 'total'
      : undefined;

    const itemStyle = {};
    if (!shouldStack && s.perCategoryColors && type === 'bar' && !horizontal) {
      // per-bar colors via data items (not used when stacked — one color per series)
    } else if (s.color) {
      itemStyle.color = s.color;
    }
    if (isCategoryTotal) {
      itemStyle.color = s.color || '#64748b';
      itemStyle.opacity = 0.92;
    }

    const series = {
      name: s.name,
      type,
      ...(stackId ? { stack: stackId } : {}),
      data: shouldStack
        ? s.data
        : s.data.map((val, i) => {
          const item = { value: val };
          if (s.perCategoryColors?.[i]) {
            item.itemStyle = { color: s.perCategoryColors[i] };
          }
          return item;
        }),
      itemStyle,
      label: shouldStack
        ? {
          ...getLabelConfig(chart),
          position: horizontal ? 'inside' : 'inside',
          fontSize: 10,
          labelLayout: { hideOverlap: true },
          ...(chart.showLabels
            ? {
              formatter: (p) => {
                const v = p?.value;
                const n = typeof v === 'number' ? v : parseFloat(v);
                if (!Number.isFinite(n) || Math.abs(n) < 0.005) return '';
                if (chartStackTo100Percent(chart)) {
                  return `${n.toFixed(0)}%`;
                }
                return currencyFormatters?.formatChartValue
                  ? currencyFormatters.formatChartValue(n)
                  : String(n);
              },
            }
            : {}),
        }
        : {
          ...getLabelConfig(chart),
          labelLayout: { hideOverlap: true },
          ...(chart.showLabels
            ? {
              formatter: (p) => {
                const n =
                  typeof p.value === 'number' ? p.value : parseFloat(p.value);
                if (!Number.isFinite(n)) return '';
                if (chartStackTo100Percent(chart)) {
                  return `${n.toFixed(0)}%`;
                }
                return currencyFormatters?.formatChartValue
                  ? currencyFormatters.formatChartValue(p.value)
                  : String(p.value);
              },
            }
            : {}),
        },
      emphasis: { focus: 'series' },
    };

    if (type === 'line') {
      series.smooth = true;
      if (isArea) {
        series.areaStyle = { opacity: 0.35 };
        series.lineStyle = { color: s.color, width: 2 };
      } else {
        series.lineStyle = { color: s.color, width: 2 };
        series.itemStyle = { color: s.color, borderColor: s.color };
      }
    }

    return series;
  });
}

export function buildBarOption(extracted, chart, currencyFormatters = null) {
  const series = buildCartesianSeries(extracted, chart, {
    defaultType: 'bar',
    currencyFormatters,
  });
  return {
    ...buildVerticalCartesianAxes(extracted, chart, {}, currencyFormatters),
    series,
  };
}

export function buildHorizontalBarOption(extracted, chart, currencyFormatters = null) {
  const series = buildCartesianSeries(extracted, chart, {
    horizontal: true,
    defaultType: 'bar',
    currencyFormatters,
  });
  const valueAxisLabel = currencyFormatters?.formatChartAxisValue
    ? { formatter: (v) => currencyFormatters.formatChartAxisValue(v) }
    : {};
  return {
    xAxis: {
      type: 'value',
      name: extracted.yAxisLabel || '',
      nameLocation: 'bottom',
      nameGap: 50,
      axisLabel: valueAxisLabel,
    },
    yAxis: {
      type: 'category',
      data: extracted.labels,
      name: extracted.xAxisLabel || '',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 36,
      inverse: true,
      axisLabel: buildCategoryAxisLabelConfig(extracted.labels || [], { horizontal: true }),
      axisTick: { alignWithLabel: true },
    },
    series: series.map((s) => ({ ...s, type: 'bar' })),
  };
}

export function buildLineOption(extracted, chart, currencyFormatters = null) {
  const series = buildCartesianSeries(extracted, chart, {
    defaultType: 'line',
    currencyFormatters,
  });
  return {
    ...buildVerticalCartesianAxes(
      extracted,
      chart,
      { boundaryGap: false },
      currencyFormatters
    ),
    series,
  };
}

export function buildAreaOption(extracted, chart, currencyFormatters = null) {
  return buildLineOption(extracted, chart, currencyFormatters);
}

function buildMixedDualGrid(chart, extracted) {
  const base = getCartesianGrid(chart, extracted, { compact: chart.compactLayout });
  return { ...base, right: '10%' };
}

export function buildMixedOption(extracted, chart, currencyFormatters = null) {
  const axes = buildVerticalCartesianAxes(extracted, chart, {}, currencyFormatters);
  const rawSeries = buildCartesianSeries(extracted, chart, {
    defaultType: 'bar',
    currencyFormatters,
  });

  const seriesMeta = extracted.series || [];
  const hasLine = seriesMeta.some((s) => resolveSeriesTypeForField(chart, s) === 'line');
  const hasBar = seriesMeta.some((s) => resolveSeriesTypeForField(chart, s) !== 'line');
  const useDualAxis = hasLine && hasBar;

  const growthPreparedByIndex = new Map();

  const series = rawSeries.map((echartsSeries, i) => {
    const meta = seriesMeta[i];
    if (!meta) return echartsSeries;
    const st = resolveSeriesTypeForField(chart, meta);
    if (st !== 'line') return echartsSeries;

    const percent = isPercentSeries(meta);
    const growthPercent = isGrowthPercentSeries(meta);
    const lineSeries = {
      ...echartsSeries,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      clip: false,
      ...(useDualAxis ? { yAxisIndex: 1 } : {}),
    };

    if (growthPercent && Array.isArray(meta.data)) {
      const prepared = prepareGrowthPercentChartData(meta.data);
      growthPreparedByIndex.set(i, prepared);
      lineSeries.data = prepared.map((p) => ({
        value: p.value,
        actualValue: p.actualValue,
      }));
    }

    if (chart.showLabels && percent) {
      lineSeries.label = {
        ...lineSeries.label,
        formatter: (p) => {
          const raw = tooltipValueFromParam(p);
          const n = typeof raw === 'number' ? raw : parseFloat(raw);
          if (!Number.isFinite(n)) return '';
          return `${n.toFixed(0)}%`;
        },
      };
    }

    return lineSeries;
  });

  if (!useDualAxis) {
    return { ...axes, series };
  }

  const lineMeta = seriesMeta.filter((s) => resolveSeriesTypeForField(chart, s) === 'line');
  const barMeta = seriesMeta.filter((s) => resolveSeriesTypeForField(chart, s) !== 'line');
  const lineIsPercent = lineMeta.some(isPercentSeries);
  const lineIsGrowthPercent = lineMeta.some(isGrowthPercentSeries);
  const lineIsBoundedPercent = lineMeta.some(isBoundedPercentSeries);

  const growthPrepared = [];
  growthPreparedByIndex.forEach((pts) => growthPrepared.push(...pts));
  const growthAxisRange =
    lineIsGrowthPercent && growthPrepared.length > 0
      ? computeGrowthPercentAxisRange(growthPrepared)
      : null;

  const rightAxisLabel =
    chart.yAxisLabelSecondary ||
    chart.y_axis_label_secondary ||
    (lineMeta.length === 1 ? lineMeta[0].name : 'Rate / %');

  const leftAxisLabel =
    chart.yAxisLabel ||
    chart.y_axis_label ||
    extracted.yAxisLabel ||
    (barMeta.length === 1 ? barMeta[0].name : 'Amount');

  const percentAxisLabel = {
    formatter: (v) => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? `${n}%` : '';
    },
    margin: 8,
    hideOverlap: true,
  };

  return {
    grid: buildMixedDualGrid(chart, extracted),
    xAxis: axes.xAxis,
    yAxis: [
      {
        ...axes.yAxis,
        position: 'left',
        name: leftAxisLabel,
      },
      {
        type: 'value',
        position: 'right',
        name: rightAxisLabel,
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 55,
        nameTextStyle: { fontWeight: 'normal', fontSize: 12 },
        ...(lineIsBoundedPercent && !lineIsGrowthPercent ? { min: 0, max: 100 } : {}),
        ...(growthAxisRange
          ? { min: growthAxisRange.min, max: growthAxisRange.max }
          : {}),
        scale: lineIsGrowthPercent && !growthAxisRange,
        axisLabel: lineIsPercent
          ? percentAxisLabel
          : currencyFormatters?.formatChartAxisValue
            ? { formatter: (v) => currencyFormatters.formatChartAxisValue(v) }
            : { margin: 8, hideOverlap: true },
        splitLine: { show: false },
      },
    ],
    series,
  };
}

/** Stacked column (vertical): categories on X, values stacked on Y */
export function buildStackedColumnOption(extracted, chart, currencyFormatters = null) {
  const series = buildCartesianSeries(extracted, chart, {
    defaultType: 'bar',
    currencyFormatters,
  });
  return {
    ...buildVerticalCartesianAxes(extracted, chart, {}, currencyFormatters),
    series,
  };
}

/** Stacked bar (horizontal): categories on Y, values stacked on X */
export function buildStackedBarOption(extracted, chart, currencyFormatters = null) {
  const series = buildCartesianSeries(extracted, chart, {
    horizontal: true,
    defaultType: 'bar',
    currencyFormatters,
  });
  const percentAxis = chartStackTo100Percent(chart);
  const valueAxisLabel = percentAxis
    ? {
      formatter: (v) => {
        const n = typeof v === 'number' ? v : parseFloat(v);
        return Number.isFinite(n) ? `${n}%` : '';
      },
    }
    : currencyFormatters?.formatChartAxisValue
      ? { formatter: (v) => currencyFormatters.formatChartAxisValue(v) }
      : {};
  return {
    xAxis: {
      type: 'value',
      name: extracted.yAxisLabel || '',
      nameLocation: 'middle',
      nameGap: 28,
      max: percentAxis ? 100 : undefined,
      axisLabel: valueAxisLabel,
    },
    yAxis: {
      type: 'category',
      data: extracted.labels,
      name: extracted.xAxisLabel || '',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 36,
      inverse: true,
      axisLabel: buildCategoryAxisLabelConfig(extracted.labels || [], { horizontal: true }),
      axisTick: { alignWithLabel: true },
    },
    series: series.map((s) => ({ ...s, type: 'bar' })),
  };
}

function sumPieSliceValues(items = []) {
  return items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
}

function buildPieSliceLabelFormatter(chart) {
  return (params) => {
    const parts = [params.name];
    if (chart.showValues || chart.showLabels) parts.push(params.value);
    if (chart.showPercentages && params.percent != null) {
      parts.push(`${params.percent}%`);
    }
    return parts.join('\n');
  };
}

/** Center / footer total for donut & funnel (matches report Grand Total of slice values). */
function buildSliceTotalGraphic(total, chart, { compact, placement }) {
  if (chart.showTotal === false) return undefined;
  const text = String(total);
  if (placement === 'donut-center') {
    return [
      {
        type: 'text',
        left: 'center',
        top: compact ? '38%' : 'middle',
        style: {
          text,
          textAlign: 'center',
          fill: chart.labelColor || '#333',
          fontSize: compact ? 16 : 20,
          fontWeight: 600,
        },
        z: 100,
      },
    ];
  }
  return [
    {
      type: 'text',
      left: 'center',
      bottom: compact ? 2 : 6,
      style: {
        text: `Total: ${text}`,
        textAlign: 'center',
        fill: chart.labelColor || '#333',
        fontSize: compact ? 12 : 14,
        fontWeight: 600,
      },
      z: 100,
    },
  ];
}

export function buildDonutOption(pieData, chart) {
  const { items } = pieData;
  const compact = chart.compactLayout;
  const total = sumPieSliceValues(items);
  const showSliceLabels =
    chart.showLabels || chart.showValues || chart.showPercentages;
  return {
    tooltip: {
      show: chart.showTooltips !== false,
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      ...getTooltipOverflowConfig(),
    },
    legend: getLegendConfig(chart, { compact }),
    graphic: buildSliceTotalGraphic(total, chart, {
      compact,
      placement: 'donut-center',
    }),
    series: [
      {
        name: chart.title || '',
        type: 'pie',
        radius: compact ? ['32%', '52%'] : ['40%', '70%'],
        center: compact ? ['50%', '42%'] : ['50%', '50%'],
        avoidLabelOverlap: true,
        label: {
          show: showSliceLabels,
          color: chart.labelColor || '#000',
          formatter: buildPieSliceLabelFormatter(chart),
        },
        data: items.map((item) => ({
          name: item.name,
          value: item.value,
          itemStyle: item.color ? { color: item.color } : undefined,
        })),
      },
    ],
  };
}

export function buildFunnelOption(pieData, chart) {
  const { items } = pieData;
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const compact = chart.compactLayout;
  const total = sumPieSliceValues(sorted);
  const showSliceLabels =
    chart.showLabels || chart.showValues || chart.showPercentages;
  return {
    tooltip: {
      show: chart.showTooltips !== false,
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)',
      ...getTooltipOverflowConfig(),
    },
    legend: getLegendConfig(chart, { compact }),
    graphic: buildSliceTotalGraphic(total, chart, {
      compact,
      placement: 'funnel-footer',
    }),
    series: [
      {
        name: chart.title || '',
        type: 'funnel',
        left: '10%',
        top: 40,
        bottom: chart.showTotal === false ? 20 : 36,
        width: '80%',
        min: 0,
        max: sorted[0]?.value || 100,
        minSize: '0%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: {
          show: showSliceLabels,
          color: chart.labelColor || '#000',
          formatter: buildPieSliceLabelFormatter(chart),
        },
        data: sorted.map((item) => ({
          name: item.name,
          value: item.value,
          itemStyle: item.color ? { color: item.color } : undefined,
        })),
      },
    ],
  };
}

export const CHART_TYPE_BUILDERS = {
  bar: buildBarOption,
  horizontalBar: buildHorizontalBarOption,
  stackedColumn: buildStackedColumnOption,
  stackedBar: buildStackedBarOption,
  line: buildLineOption,
  area: buildAreaOption,
  mixed: buildMixedOption,
  donut: null,
  funnel: null,
};
